// Ancla temporal autoritativa para la agenda pública.
//
// El generador de slots corre en el navegador, pero el reloj del dispositivo no
// es fuente de verdad: puede estar mal puesto, o movido a propósito para que
// aparezcan slots que la anticipación mínima debería ocultar. Resolver todo en
// el timezone del negocio corrige el HUSO, no un reloj desajustado.
//
// El patrón: se pide la hora al servidor una vez (`public.booking_server_now()`)
// y se guarda junto a una lectura de reloj MONOTÓNICO (`performance.now()`).
// A partir de ahí el tiempo avanza sumando el monotónico transcurrido -- que no
// se ve afectado si el usuario cambia la hora del sistema a mitad de sesión.

export interface ServerClockAnchor {
  /** Hora del servidor en el momento del anclaje, en epoch ms. */
  serverEpochMs: number;
  /** Lectura del reloj monotónico en ese mismo instante. */
  monotonicMs: number;
}

/** Edad máxima del ancla antes de pedir una nueva. */
export const ANCHOR_MAX_AGE_MS = 5 * 60 * 1000;

export function createAnchor(serverNow: Date, monotonicMs: number): ServerClockAnchor {
  return { serverEpochMs: serverNow.getTime(), monotonicMs };
}

/**
 * Hora actual derivada del ancla.
 *
 * Devuelve `null` si no hay ancla: quien llama NO debe caer de vuelta a
 * `new Date()`. Sin hora autoritativa no se puede decidir qué slots son
 * elegibles, y mostrarlos igual sería exactamente el fallo que esto evita.
 */
export function resolveNow(
  anchor: ServerClockAnchor | null | undefined,
  monotonicMs: number,
): Date | null {
  if (!anchor) return null;

  const elapsed = monotonicMs - anchor.monotonicMs;
  // Un monotónico que retrocede solo puede venir de un reloj no monotónico
  // real; se trata como ancla inservible en vez de restar tiempo.
  if (!Number.isFinite(elapsed) || elapsed < 0) return null;

  return new Date(anchor.serverEpochMs + elapsed);
}

/** ¿Conviene volver a pedir la hora al servidor? */
export function isAnchorStale(
  anchor: ServerClockAnchor | null | undefined,
  monotonicMs: number,
  maxAgeMs: number = ANCHOR_MAX_AGE_MS,
): boolean {
  if (!anchor) return true;
  const elapsed = monotonicMs - anchor.monotonicMs;
  if (!Number.isFinite(elapsed) || elapsed < 0) return true;
  return elapsed > maxAgeMs;
}

/** Lectura monotónica, con degradado si `performance` no está disponible. */
export function readMonotonic(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}
